import {Component, effect, inject, model} from '@angular/core';
import {
  dateObjectToISOString,
  EControlActions,
  EsvgFiles,
  IControlObject,
  INITIAL_STATE_TOKEN,
} from "frontier/nucleus";
import {
  DELETE_ACTION,
  DialogService,
  ITableAction,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
  ToolbarStore
} from "frontier/browserkit";
import {GridOptions} from "ag-grid-community";
import {concatMap, Observable, of, startWith, switchMap} from "rxjs";
import {
  NewPermanentOrdinanceDialogComponent
} from "./new-permanent-ordinance-dialog/new-permanent-ordinance-dialog.component";
import {INewPermanentOrdinanceDialog} from "./new-permanent-ordinance-dialog/new-permanent-ordinance-dialog.interface";
import {filter, finalize, tap} from "rxjs/operators";
import {ProcessOverviewStore} from '../../service/process-overview.store';
import {toObservable} from '@angular/core/rxjs-interop';
import {PermanentOrdinanceForClientService} from 'src/app/api/generated';
import AppControlGuids from '../../../../../api/ControlGuids';

@Component({
  selector: 'kpi4me-permanent-ordinance',
  templateUrl: '../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrls: [
    '../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss'
  ],
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: PermanentOrdinanceComponent},
  ],
})
export class PermanentOrdinanceComponent extends TableControlComponent {
  private _processOverviewService = inject(ProcessOverviewStore);
  private _toolbarStore = inject(ToolbarStore);
  private _dialogService = inject(DialogService);
  private _permanentOrdinanceForClientService = inject(PermanentOrdinanceForClientService);
  selectedClient = this._processOverviewService.selectedClient;
  selectedClient$ = toObservable(this.selectedClient).pipe(startWith(null));
  selectedCart = this._processOverviewService.selectedCart;
  selectedCart$ = toObservable(this.selectedCart).pipe(startWith(null));
  toolbarFilterDto = this._toolbarStore.toolbarFilterDto;
  override GUID = AppControlGuids.TPermanentOrdinanceForClient;
  override actions = model<ITableAction[]>([
    {
      displayName: 'Neue Dauerverordnung anlegen',
      controlAction: EControlActions.newOrdinance,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.add,
      action: () => this.createNewPermanentOrdinance(),
    },
    DELETE_ACTION,
  ]);
  override gridOptions: GridOptions = {
    ...SharedGridOptions,
  };

  constructor() {
    super();
    effect(() => {
      const selectedClient = this.selectedClient();
      const selectedCart = this.selectedCart();
      const toolbarFilterDto = this.toolbarFilterDto();
      if (toolbarFilterDto && (selectedClient || selectedCart)) {
        this.patchFilter({
          ...toolbarFilterDto,
          parent: selectedClient ? selectedClient.obj : selectedCart.obj,
          typename: selectedClient ? 'client' : 'cart'
        })
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter(evt => {
          return new Map([
            [AppControlGuids.TAllClientList, new Set([EControlActions.attendance])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap(() => this.changeAndFetchInstance())).subscribe(),
    );
  }

  private createNewPermanentOrdinance(): Observable<any> {
    return this._permanentOrdinanceForClientService.permanentOrdinanceForClientgetRepository({
      InstanceId: this.instanceId(),
      AttributeName: 'ItemGroup',
      AttributeIdx: -1,
    }).pipe(
      switchMap((itemGroups) => {
        return this.openCreatePermanentOrdinanceDialog(itemGroups);
      })
    );
  }

  openCreatePermanentOrdinanceDialog(itemGroups: IControlObject[]): Observable<any> {
    let documentData: any;
    const dialogRef = this._dialogService.dialog.open(NewPermanentOrdinanceDialogComponent, {
      data: {
        itemGroups: itemGroups.map(ig => ({
          name: ig.name,
          value: ig.signature,
        })),
      } as INewPermanentOrdinanceDialog,
    });
    return dialogRef.afterClosed().pipe(
      finalize(() => {
        this.gridApi?.hideOverlay();
      }),
      concatMap((formData: INewPermanentOrdinanceDialog) => {
        if (formData) {
          documentData = formData.document;
          return this._permanentOrdinanceForClientService.permanentOrdinanceForClientNewPermanentOrdinance(
            {
              InstanceId: this.instanceId(),
              ItemGroup: itemGroups.find(ig => {
                return ig.signature === formData.itemGroup;
              }),
              StartDate: dateObjectToISOString(formData.fromDate),
              EndDate: dateObjectToISOString(formData.toDate),
            }
          );
        }
        return of(null);
      }),
      concatMap((ordinanceObj: any) => {
        // We look for the attribute, that has an object of type 'TDocument' as its value, since this key is needed to
        // call 'UploadDocument'
        let propertyname: string;
        for (let key in ordinanceObj) {
          if (ordinanceObj.hasOwnProperty(key) &&
            typeof ordinanceObj[key] === 'object' &&
            ordinanceObj[key]?.type &&
            ordinanceObj[key]?.type === 'TDocument') {
            propertyname = key;
          }
        }
        return ordinanceObj && propertyname ? this.controlService.controlPostUploadDocument(
          documentData.name,
          documentData.fileFormate,
          ordinanceObj.rowid.toString(),
          ordinanceObj.type,
          ordinanceObj.signature,
          propertyname,
          documentData.data,
        ).pipe(
          finalize(() => {
            this.changeAndFetchInstance();
          }),
        ) : of(null);
      }),
    );
  }
}
